import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import useLaunchDarklyFlags from "@MasterBluePrint/Common/Hooks/useLaunchDarklyFlags";
import { LaunchDarklyFlagKeys } from "@MasterBluePrint/Common/Utilities/LaunchDarklyFlagKeys";
import loadInventoryForDataLayer from "@MasterBluePrint/Handlers/LoadInventoryForDataLayer";
import React, { FC } from "react";
import { ResolveThunks,connect } from "react-redux";
import { css } from "styled-components";


interface OwnProps {
    productNumber: string;
    customerProductNumber?: string;
    manufacturerItem?: string;
    showCustomerName?: boolean;
    showLabel?: boolean;
    showManufacturerItem?: boolean;
    extendedStyles?: ProductPartNumbersStyles;
    showOurPart?: boolean;
    showMfrPart?: boolean;
    product?: ProductContextModel;
    fromPage?: string;    
}

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const currentPageUrl = location.pathname;
    const path = location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname;
    const catalogPage = getCatalogPageStateByPath(state, path).value;
    return {
        category: getCategoryState(state, catalogPage?.categoryIdWithBrandId ?? catalogPage?.categoryId).value,
        currentPageUrl
    };
};

const mapDispatchToProps = {
    loadInventoryForDataLayer,
};

 type Props = OwnProps  & ReturnType<typeof mapStateToProps>  & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductPartNumbersStyles {
    condensed?: boolean;
    container?: GridContainerProps;
    erpNumberGridItem?: GridItemProps;
    erpNumberLabelText?: TypographyPresentationProps;
    erpNumberLink?: LinkPresentationProps;
    erpNumberValueText?: TypographyPresentationProps;
    customerNameGridItem?: GridItemProps;
    customerNameLabelText?: TypographyPresentationProps;
    customerNameValueText?: TypographyPresentationProps;
    manufacturerItemGridItem?: GridItemProps;
    manufacturerItemLabelText?: TypographyPresentationProps;
    manufacturerItemValueText?: TypographyPresentationProps;
    manufacturerItemLink?: LinkPresentationProps;
    partContainer?: InjectableCss;
}

export const productPartNumbersStyles: ProductPartNumbersStyles = {
    container: { gap: 0 },
    erpNumberLabelText: {
        weight: "bold",
        css: css`
            flex: 0 0 auto;
            display: block;
            color: #002554;
            font-family: Roboto;
            font-size: .875rem;
            letter-spacing: .028rem;
            line-height: 1.25rem;

            @media screen and (max-width: 1399px) {
                display: inline-block;
                margin-right: .625rem;            
                font-family: Roboto;
                font-size: .875rem;
                font-weight: bold;
                letter-spacing: .028rem; 
                margin-bottom: .31250625rem;
            }
        `,
    },
    erpNumberValueText: {
        css: css`
            flex: 1 1 auto;
            min-width: 0;
            ${wrapInContainerStyles}
            display: block;
            color: #333F48;
            font-family: Roboto;
            font-size: .875rem;
            letter-spacing: .028rem;
            line-height: 1.25rem;
            
            @media screen and (max-width: 1399px) {
                display: block;
                margin-bottom: .31250625rem;      
                   
            }
        `,
    },
    erpNumberLink: {
        typographyProps: {
            css: css`
                width: 100%;
                ${wrapInContainerStyles}
            `,
        },
    },
    erpNumberGridItem: {
        css: css`
            display: block;
            color: #333F48;
            font-family: Roboto;
            font-size: .875rem;
            letter-spacing: .028rem;
            line-height: 1.25rem;

            @media screen and (max-width: 1399px) {
                display: inline-block;
                margin-bottom: .31250625rem;    
            }

            @media screen and (min-width: 1400px) {
                max-width: 7.5rem;
            }
        `,
    },
    customerNameLabelText: {
        weight: "bold",
        css: css`
            display: block;
            flex: 0 0 auto;
            color: #002554;
            font-family: Roboto;
            font-size: .875rem;
            letter-spacing: .028rem;
            line-height: 1.25rem;

            @media screen and (max-width: 1399px) {
                display: inline-block;
                margin-right: .625rem;
                color: #002554;
                font-family: Roboto;
                font-size: .875rem;
                font-weight: bold;
                letter-spacing: .028rem; 
                margin-bottom: .31250625rem;             
            }
        `,
    },
    customerNameValueText: {
        css: css`
            display: block;
            flex: 1 1 auto;
            min-width: 0;
            ${wrapInContainerStyles}
            color: #333F48;
            font-family: Roboto;
            font-size: .875rem;
            letter-spacing: .028rem;
            line-height: 1.25rem;
            
            @media screen and (max-width: 1399px) {
                display: block;
                margin-bottom: .31250625rem;     
                          
            }
        `,
    },
    manufacturerItemGridItem: {
        css: css`
            display: block;
            color: #333F48;
            font-family: Roboto;
            font-size: .875rem;
            letter-spacing: .028rem;
            line-height: 1.25rem;

            @media screen and (max-width: 1399px) {
                display: inline-block;
                margin-bottom: .31250625rem;    
            }

            @media screen and (min-width: 1400px) {
                max-width: 7.5rem;
            }
        `,
    },
    manufacturerItemLabelText: {
        weight: "bold",
        css: css`
            display: block;
            flex: 0 0 auto;
            color: #002554;
            font-family: Roboto;
            font-size: .875rem;
            letter-spacing: .028rem;
            line-height: 1.25rem;

            @media screen and (max-width: 1399px) {
                display: inline-block;
                margin-right: .625rem;
                color: #002554;
                font-family: Roboto;
                font-size: .875rem;
                font-weight: bold;
                letter-spacing: .028rem;
                margin-bottom: .31250625rem;              
            }
        `,
    },
    manufacturerItemValueText: {
        css: css`
            display: block;
            flex: 1 1 auto;
            min-width: 0;
            ${wrapInContainerStyles}
            color: #333F48;
            font-family: Roboto;
            font-size: .875rem;
            letter-spacing: .028rem;
            line-height: 1.25rem;
            
            @media screen and (max-width: 1399px) {
                display: block;
                margin-bottom: .31250625rem;      
            }
        `,
    },
    manufacturerItemLink: {
        typographyProps: {
            css: css`
                width: 100%;
                ${wrapInContainerStyles}
            `,
        },
    },
    partContainer: {
        css: css`
            display: block;
            @media screen and (min-width: 1400px) {
                max-width: 7.5rem;
            }
        `,
    }
};

const ProductPartNumbers: FC<Props> = ({
    productNumber,
    customerProductNumber,
    manufacturerItem,
    showCustomerName = true,
    showManufacturerItem = true,
    showLabel = true,
    extendedStyles,
    showOurPart = true,
    showMfrPart = true,
    product,
    fromPage,   
    currentPageUrl,   
    category,
    loadInventoryForDataLayer
}) => {
    const styles = mergeToNew(productPartNumbersStyles, extendedStyles);
    const displayManufacturerNumber = showManufacturerItem && manufacturerItem;
    const displayCustomerNumber = showCustomerName && customerProductNumber;
    const numberOfItems = 1 + (displayManufacturerNumber ? 1 : 0) + (displayCustomerNumber ? 1 : 0);
    const mobileGridItemWidth = numberOfItems > 1 ? 6 : 12;
    const desktopGridItemWidth = 12 / numberOfItems;
    const gridItemWidth = styles.condensed
        ? [mobileGridItemWidth, mobileGridItemWidth, mobileGridItemWidth, mobileGridItemWidth, desktopGridItemWidth]
        : 12;
    let featureFlags: string[] = [LaunchDarklyFlagKeys.TEUS_8655];
    const flagValues = useLaunchDarklyFlags({ featureFlags });
    const isFeatureOn = flagValues[LaunchDarklyFlagKeys.TEUS_8655];
    const productDetailPath = product?.productInfo?.productDetailPath || product?.product?.canonicalUrl;
    const isFromProductListPage = fromPage === "productlist";
    const productId = product?.product.id;
    const unitOfMeasure = product?.productInfo?.unitOfMeasure || "";

    const productClickDataLayerEvent = (itemLinkType: string) => {
        if (productId) {
            loadInventoryForDataLayer({
                productId,
                listType: '',
                unitOfMeasure,
                product,
                currentPageUrl,
                carouselTitle:'',
                category,
                itemLinkClick: itemLinkType,
            });
        }
    };

    return (
        <GridContainer {...styles.container}>
            {showMfrPart && displayManufacturerNumber && (
                <GridItem width={gridItemWidth as GridWidths} {...styles.manufacturerItemGridItem} className="mfg-part-container">
                    {showLabel && <Typography {...styles.manufacturerItemLabelText}>{translate("MFG #")}</Typography>}
                    {!showLabel && (
                        <VisuallyHidden>
                            <Typography {...styles.manufacturerItemLabelText}>{translate("MFG #")}</Typography>
                        </VisuallyHidden>
                    )}
                    { (isFeatureOn && isFromProductListPage) ? (
                        <Link {...styles.manufacturerItemLink} 
                            href={productDetailPath}
                            data-test-selector="productMfrPartNumberLink"
                            onClick={() => productClickDataLayerEvent("Manufacturer Part Number")}
                            className="product-mfrpartnumber-link"
                        >
                            {manufacturerItem}
                        </Link>
                    )
                    :
                    (
                        <Typography {...styles.manufacturerItemValueText}>{manufacturerItem}</Typography>
                    )}
                </GridItem>
            )}
            {showOurPart && (
                <>
                    <GridItem width={gridItemWidth as GridWidths} {...styles.erpNumberGridItem} className="part-number-container">
                        {showLabel && <Typography {...styles.erpNumberLabelText}>{translate("Part #")}</Typography>}
                        {!showLabel && (
                            <VisuallyHidden>
                                <Typography {...styles.erpNumberLabelText}>{translate("Part #")}</Typography>
                            </VisuallyHidden>
                        )}
                        { (isFeatureOn && isFromProductListPage) ? (
                            <Link {...styles.erpNumberLink} 
                                href={productDetailPath} 
                                data-test-selector="productPartNumberLink"
                                onClick={() => productClickDataLayerEvent("TE Part Number")}
                                className="product-partnumber-link"
                            >
                                {productNumber}
                            </Link>
                        )
                        :
                        (
                        <Typography {...styles.erpNumberValueText} data-test-selector="productNumber">
                            {productNumber}
                        </Typography>
                        )}
                    </GridItem>
                    {displayCustomerNumber && (
                        <GridItem width={gridItemWidth as GridWidths} {...styles.customerNameGridItem} {...styles.partContainer} className="customer-part-container">
                            {showLabel && <Typography {...styles.customerNameLabelText}>{translate("My Part #")}</Typography>}
                            {!showLabel && (
                                <VisuallyHidden>
                                    <Typography {...styles.customerNameLabelText}>{translate("My Part #")}</Typography>
                                </VisuallyHidden>
                            )}
                            <Typography {...styles.customerNameValueText}>{customerProductNumber}</Typography>
                        </GridItem>
                    )}
                </>
            )}
        </GridContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductPartNumbers);
